import { useVirtualizer } from "@tanstack/react-virtual";
import { Loader2 } from "lucide-react";
import { useLayoutEffect, useMemo, useRef } from "react";
import { AiChatEmptyState } from "./ai-chat-empty-state";
import {
  AiChatAssistantMessage,
  AiChatMessageRow,
  AiChatUserMessage,
} from "./ai-chat-message";
import type { AiChatMessage } from "./ai-chat-model";
import type { AiChatPrompt } from "./ai-chat-prompts";

const MESSAGE_ESTIMATE_PX = 120;
const MESSAGE_OVERSCAN = 8;
const STICK_TO_BOTTOM_PX = 64;

interface AiChatThreadProps {
  messages: AiChatMessage[];
  pendingQuestion: string | null;
  streamingAnswer: string;
  isStreaming: boolean;
  isLoadingHistory: boolean;
  error: boolean;
  isEmpty: boolean;
  teenName: string;
  onSelectPrompt: (prompt: AiChatPrompt, label: string) => void;
  onRetry: () => void;
}

type ThreadRow =
  | { key: string; kind: "message"; message: AiChatMessage }
  | { key: "pending"; kind: "pending"; text: string }
  | { key: "draft"; kind: "draft"; text: string; pending: boolean }
  | { key: "error"; kind: "error" };

export function AiChatThread({
  messages,
  pendingQuestion,
  streamingAnswer,
  isStreaming,
  isLoadingHistory,
  error,
  isEmpty,
  teenName,
  onSelectPrompt,
  onRetry,
}: AiChatThreadProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const prevPendingQuestion = useRef<string | null>(null);

  const hasPendingQuestion = pendingQuestion !== null;
  const showAssistantDraft = isStreaming || streamingAnswer.length > 0;

  const rows = useMemo<ThreadRow[]>(() => {
    const next: ThreadRow[] = messages.map((message) => ({
      key: message.id,
      kind: "message",
      message,
    }));
    if (hasPendingQuestion) {
      next.push({ key: "pending", kind: "pending", text: pendingQuestion });
    }
    if (showAssistantDraft) {
      next.push({
        key: "draft",
        kind: "draft",
        text: streamingAnswer,
        pending: isStreaming,
      });
    }
    if (error) next.push({ key: "error", kind: "error" });
    return next;
  }, [
    messages,
    hasPendingQuestion,
    pendingQuestion,
    showAssistantDraft,
    streamingAnswer,
    isStreaming,
    error,
  ]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => MESSAGE_ESTIMATE_PX,
    overscan: MESSAGE_OVERSCAN,
    getItemKey: (index) => rows[index]?.key ?? index,
  });
  const totalSize = virtualizer.getTotalSize();

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottom.current = distanceFromBottom <= STICK_TO_BOTTOM_PX;
  };

  useLayoutEffect(() => {
    if (hasPendingQuestion && prevPendingQuestion.current === null) {
      stickToBottom.current = true;
    }
    prevPendingQuestion.current = pendingQuestion;

    const el = scrollRef.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [totalSize, hasPendingQuestion, pendingQuestion, streamingAnswer, error]);

  const showInitialLoader = isLoadingHistory && isEmpty;
  const showEmptyState = isEmpty && !showInitialLoader;

  if (showInitialLoader) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2
          className="size-5 animate-spin text-icon-secondary"
          aria-hidden
        />
      </div>
    );
  }

  if (showEmptyState) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <AiChatEmptyState teenName={teenName} onSelectPrompt={onSelectPrompt} />
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="subtle-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto px-4 py-4"
    >
      <div className="relative w-full" style={{ height: `${totalSize}px` }}>
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const row = rows[virtualRow.index];
          if (!row) return null;
          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              className="absolute left-0 top-0 w-full pb-4"
              style={{ transform: `translateY(${virtualRow.start}px)` }}
            >
              <ThreadRowView row={row} onRetry={onRetry} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ThreadRowView({
  row,
  onRetry,
}: {
  row: ThreadRow;
  onRetry: () => void;
}) {
  switch (row.kind) {
    case "message":
      return <AiChatMessageRow message={row.message} />;
    case "pending":
      return <AiChatUserMessage text={row.text} />;
    case "draft":
      return <AiChatAssistantMessage text={row.text} pending={row.pending} />;
    case "error":
      return (
        <div className="flex flex-col items-start gap-2 rounded-2xl border border-surfaces-divider bg-surfaces-z0 px-4 py-3">
          <p className="font-body text-body-2 text-states-error">
            Something went wrong generating a response.
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="font-body text-body-3 text-states-hyperlinked transition-opacity hover:opacity-80 focus-visible:outline-none"
          >
            Try again
          </button>
        </div>
      );
    default: {
      const exhaustive: never = row;
      return exhaustive;
    }
  }
}
